import FaqPageContainer from '@/app/components/FaqPageComponents/FaqPageContainer';
import { Accordion, FaqCategories } from '@/app/shared/ui/Accordion';

const FaqPage = () => {
  return <FaqPageContainer centerContent={<Accordion items={FAQ_DATA} />} />;
};

export default FaqPage;

const FAQ_DATA: FaqCategories = {
  'General': [
    {
      title: 'What is Mezzanine?',
      content: [
        'Mezzanine is where DeFi strategies get split by risk. Each strategy runs in its own vault, and each vault is divided into tranches. One tranche takes losses first and earns a larger share of the yield. The others sit behind it, earning less in exchange for a buffer beneath them.',
        'You pick the strategy, then you pick where you sit in it.',
      ],
    },
    {
      title: 'Who is Mezzanine for?',
      content: [
        'Anyone who wants exposure to a strategy at a risk level that suits them rather than the one the strategy happens to come with.',
        'That covers depositors looking for higher yield and willing to take first-loss exposure, and depositors who want a lower-risk position in the same strategy.',
      ],
    },
    {
      title: 'What problem does Mezzanine solve?',
      content: [
        'Most yield products come at one risk level. You either accept it or you go elsewhere, which means trust and risk preference get bundled into a single decision.',
        'Tranching separates them. You can stay with a strategy you like and still choose how much risk you take.',
      ],
    },
    {
      title: 'What is a strategy vault?',
      content: [
        "A vault holding one asset or strategy, with its own assets, NAV, fees, tranches, and risk parameters. Vaults are isolated from each other. A loss in one vault is absorbed by that vault's tranches and doesn't touch any other.",
      ],
    },
    {
      title: 'Where can I learn more or follow updates?',
      content: [
        "The docs cover everything here in full detail, including each vault's parameters and risks. For updates and questions, we're on X, Discord, and Telegram. Links in the footer.",
      ],
    },
  ],
  'Vaults': [
    {
      title: 'What strategies are available?',
      content: [
        'Each live vault has its own page with its strategy, assets, protocols, chains, fees, and risks. Vaults are added over time and each one is documented separately.',
      ],
    },
    {
      title: 'Why does each strategy have its own vault?',
      content: [
        'So risk stays where you put it. Isolation means a strategy that goes wrong affects the people who chose it, not everyone on the platform.',
        'It also lets each vault set parameters that suit its strategy: withdrawal periods, epoch length, yield allocation, and reserve requirements all differ by vault.',
      ],
    },
    {
      title: 'What is the base token?',
      content: [
        "Every vault has a base token, written as mxVault, for example mxPrime for the Prime Vault. It's the accounting layer between your deposit asset and your tranche token. It doesn't earn yield.",
      ],
    },
    {
      title: 'Do I need to hold the base token?',
      content: [
        'No. Deposits and withdrawals bundle the conversion into a single transaction, so you go from your deposit asset straight into the tranche you chose.',
      ],
    },
    {
      title: 'Which chain do I deposit on?',
      content: [
        'Each vault has one deposit chain, listed on its page. The strategy may hold positions on other chains, which is also disclosed.',
      ],
    },
  ],
  'Tranches': [
    {
      title: 'What are tranches?',
      content: [
        'Layers within a vault that determine the order in which losses are absorbed and how yield is divided.',
        'The lowest layer takes losses first and receives a larger share of the yield for it. Each layer above is affected only when the one below is exhausted.',
      ],
    },
    {
      title: 'What is the Junior tranche?',
      content: [
        "The first-loss position. Junior absorbs strategy losses before any other tranche and receives a larger share of the vault's yield as compensation.",
        'Junior tokens are written as mjVault, for example mjPrime for the Prime Vault.',
      ],
    },
    {
      title: 'What is the Senior tranche?',
      content: [
        'The lowest-risk yield-bearing position. Senior only takes losses once Junior, and the Mezzanine tranche where enabled, have been depleted. In exchange for that buffer it takes a smaller share of the yield.',
        'Senior tokens are written as msVault, so for example msPrime.',
      ],
    },
    {
      title: 'What is the Mezzanine tranche?',
      content: [
        'An optional middle position, sitting between Junior and Senior in the loss order. It generally carries a longer withdrawal period. Not every vault enables it.',
      ],
    },
    {
      title: 'How is risk different between tranches?',
      content: [
        'By loss order and by concentration. Junior absorbs losses first, and because it usually holds less capital than Senior, a given vault loss removes a larger proportion of it.',
        "Every tranche remains exposed to the underlying strategy. Tranching changes how losses are distributed. It doesn't prevent them.",
      ],
    },
    {
      title: 'Which tranche should I choose?',
      content: [
        "That's your decision to make, based on the balance you want between yield and risk.",
        'Junior is priced to earn more and can lose a substantial share of its value in a bad event. Senior takes a smaller share of the yield and has a buffer beneath it, though it can still take losses.',
      ],
    },
  ],
  'Yield': [
    {
      title: 'Where does yield come from?',
      content: [
        "From the underlying strategy. Mezzanine doesn't generate yield; it divides what the strategy produces.",
        'Each vault page states its strategy and where its return comes from.',
      ],
    },
    {
      title: 'How is yield distributed?',
      content: [
        'Net yield, after fees, splits into two parts.',
        'The base reward goes to all yield-bearing tranches in proportion to the capital in each, so every tranche earns it at the same rate per dollar.',
        'The risk premium is carved out of the same pot and paid only to the tranches that take losses first.',
        "The two always sum to the vault's net yield.",
      ],
    },
    {
      title: 'What is base reward vs risk premium?',
      content: [
        'The base reward pays you for supplying capital. The risk premium pays you for standing in front of other tranches in case something goes wrong.',
        "Base is not a rate. It's the share of yield that isn't premium.",
      ],
    },
    {
      title: 'Why does the Junior tranche earn more?',
      content: [
        'Two reasons.',
        "It receives the risk premium, which Senior doesn't. And because Junior holds less capital, that premium is concentrated across a smaller base, producing a higher rate per dollar.",
      ],
    },
    {
      title: "Why did my rate change when I didn't do anything?",
      content: [
        "The rate shown when you deposited isn't fixed for the life of your position. Two things move it.",
        "The strategy's performance. Your rate rises and falls with what the strategy earns.",
        'Other people depositing or withdrawing. The risk premium is shared between everyone in the tranche, and the more capital enters, the less each depositor receives. When capital enters, your rate falls. When capital leaves, your rate rises.',
      ],
    },
    {
      title: 'Can yields change over time?',
      content: [
        "Yes. Yield varies with the strategy's performance and with the composition of the vault. Nothing is fixed and nothing is guaranteed.",
      ],
    },
  ],
  'deposits and withdrawals': [
    {
      title: 'How do I enter a tranche?',
      content: [
        'Choose a vault, choose a tranche, enter an amount, approve and confirm. You receive the tranche token representing your position. The base token conversion happens inside the same transaction.',
      ],
    },
    {
      title: 'How do I exit a tranche?',
      content: [
        "Submit a withdrawal request, wait for the vault's withdrawal period, then finalize to receive the vault's redemption asset.",
      ],
    },
    {
      title: 'What is an epoch?',
      content: [
        "A vault's accounting cycle. Withdrawal periods and yield distribution are measured in epochs, and epoch length is set per vault. For example the Prime Vault uses a one-week epoch.",
      ],
    },
    {
      title: 'Why is my wait longer than the stated withdrawal period?',
      content: [
        'Because a request takes effect at the end of the epoch it was made in, not immediately. So the wait is the time left in the current epoch plus the withdrawal period.',
        'The app shows your exact unlock date when you submit the request.',
      ],
    },
    {
      title: 'What happens during the withdrawal period?',
      content: [
        "Your position stays in its original place in the loss waterfall until the withdrawal completes, so it's still exposed. You may continue receiving a portion of yield, at a rate set by the vault. The final amount depends on the tranche's value when the withdrawal finalizes.",
      ],
    },
    {
      title: 'Why is there a wait at all?',
      content: [
        'Because the strategy needs time to unwind positions and make capital available. Strategies holding less liquid positions generally have longer withdrawal periods.',
      ],
    },
    {
      title: 'What are the fees?',
      content: [
        'A performance fee, taken from the yield the strategy earns. Nothing is charged on your principal.',
        'The standard rate is 20% of strategy yield. A vault may run a discounted introductory rate for a defined period, and any vault-specific fees beyond that are disclosed on the vault page before you deposit.',
      ],
    },
  ],
  'security and risk': [
    {
      title: 'Can I lose money?',
      content: [
        'Yes. Every tranche is exposed to the underlying strategy, and no tranche guarantees a positive return or the return of principal.',
        "What the tranches change is the order. Junior takes losses first, and the positions behind it are only affected once it's exhausted. How that works is covered in the next two questions.",
      ],
    },
    {
      title: 'What happens if the strategy loses money?',
      content: [
        'Losses are applied within that vault, starting with the Junior tranche, then Mezzanine where enabled, then Senior, then the base token.',
        'Losses reduce the value of each tranche token rather than the number you hold. Your balance stays the same; what it converts to falls.',
      ],
    },
    {
      title: 'Can a loss in one vault affect another?',
      content: [
        "Not through the tranche structure. Each vault's losses are contained within its own tranches.",
        'Vaults do share some infrastructure, including automation and oracle providers, so a failure in shared infrastructure could affect multiple vaults.',
      ],
    },
    {
      title: 'What is the execution reserve?',
      content: [
        'Capital set aside per vault to cover approved execution costs like slippage and price impact when entering, adjusting, or exiting positions.',
        "It doesn't cover strategy losses. Those go through the tranche waterfall, as do execution costs that exceed the reserve.",
      ],
    },
    {
      title: 'Who controls strategy selection?',
      content: [
        'Vaults are curated by external curators. Strategies run within predefined parameters covering approved assets and protocols, exposure and leverage limits, and execution bounds.',
      ],
    },
    {
      title: 'How transparent is Mezzanine?',
      content: [
        'Each vault page publishes its contract addresses, assets held, external positions, allocations, NAV history, fees, and reserve usage.',
        'Mezzanine uses Accountable for independent verification of assets and reporting.',
      ],
    },
    {
      title: 'Can I see where funds are deployed?',
      content: [
        'Yes. Vault and strategy contract addresses, current allocations, and external protocol positions are published per vault.',
      ],
    },
    {
      title: 'Is Mezzanine audited?',
      content: [
        'Yes. Manual audits by ChainSecurity and Sherlock, formal verification by Certora, pre-audit review by Woof and Dingbats, and an operational security audit by OpSek. A crowdsourced audit and bug bounty with Sherlock are planned.',
        "Audits cover the contracts and versions within their stated scope. An audit doesn't guarantee a protocol is free from vulnerabilities.",
      ],
    },
    {
      title: 'Who can access Mezzanine?',
      content: [
        "Access is restricted in some jurisdictions, including the United States and United Kingdom. Individual vaults may apply additional requirements; where a curator requires identity verification it's disclosed on the vault page.",
      ],
    },
  ],
};
